import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  filterAndSortRows,
  rowSearchText,
} from '../../pages/AdminPages/clientTable';
import { api, apiEndpoints } from '../../utils/apiService';
import { type TableSortState, toggleSortState } from '../../utils/listOrdering';
import ComponentCard from '../common/ComponentCard';
import SearchableSelect from '../common/SearchableSelect';
import Label from '../form/Label';
import Input from '../form/input/InputField';
import { Modal } from '../ui/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../ui/table';

const NEW_CONTACT = '__new__';
const STAFF_MESSAGE = 'staff_message';

const asList = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  const results = (data as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? results : [];
};
const ALL_EVENTS = 'all_events';

interface NotificationStore {
  id: number;
  name: string;
  takes_events?: boolean;
  takes_catering?: boolean;
}

interface NotificationTarget {
  id: number;
  store: number;
  channel: 'email' | 'sms';
  email: string | null;
  phone_e164: string | null;
  label: string;
  is_enabled: boolean;
}

interface NotificationRuleTarget {
  id?: number;
  channel: 'email' | 'sms';
  email: string | null;
  phone_e164: string | null;
  label?: string | null;
  is_enabled?: boolean;
}

interface NotificationRule {
  id: number;
  store: number;
  event_type: string;
  channel: 'email' | 'sms';
  is_enabled: boolean;
  category_label: string;
  category_description: string;
  additional_context: string;
  routing_priority: number;
  applies_to_all_voicemails?: boolean;
  targets?: NotificationRuleTarget[];
}

interface EventTypeOption {
  value: string;
  label: string;
}

interface SubscriptionRow {
  target: NotificationTarget;
  events: Array<{ rule: NotificationRule; label: string }>;
}

interface EditorState {
  open: boolean;
  mode: 'create' | 'edit';
  storeId: number | '';
  contactChoice: string;
  label: string;
  channel: 'email' | 'sms';
  email: string;
  phone: string;
  eventTypes: string[];
  voicemailScope: 'all' | 'category';
  categoryLabel: string;
  categoryDescription: string;
  additionalContext: string;
  routingPriority: string;
  categoryRuleId: number | null;
  allVoicemailRuleId: number | null;
  initialVoicemailScope: 'all' | 'category';
}

const emptyEditor = (storeId: number | '' = ''): EditorState => ({
  open: false,
  mode: 'create',
  storeId,
  contactChoice: '',
  label: '',
  channel: 'email',
  email: '',
  phone: '',
  eventTypes: [],
  voicemailScope: 'all',
  categoryLabel: '',
  categoryDescription: '',
  additionalContext: '',
  routingPriority: '0',
  categoryRuleId: null,
  allVoicemailRuleId: null,
  initialVoicemailScope: 'all',
});

const buttonClassName =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60';
const quietButtonClassName =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700';
const fieldClassName =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';

async function readError(
  response: Response | null | undefined,
  fallback: string,
) {
  if (!response) return fallback;
  const data = await response.json().catch(() => null);
  if (!data || typeof data !== 'object') return fallback;
  if (typeof data.detail === 'string') return data.detail;
  const parts = Object.entries(data).map(([field, message]) => {
    const text = Array.isArray(message)
      ? message.join(', ')
      : typeof message === 'string'
        ? message
        : JSON.stringify(message);
    return `${field}: ${text}`;
  });
  return parts.length > 0 ? parts.join(' ') : fallback;
}

function contactValue(target: {
  channel: 'email' | 'sms';
  email: string | null;
  phone_e164: string | null;
}) {
  return target.channel === 'sms'
    ? target.phone_e164 || ''
    : target.email || '';
}

function contactLabel(target: {
  label?: string | null;
  channel: 'email' | 'sms';
  email: string | null;
  phone_e164: string | null;
}) {
  const name = target.label?.trim() || 'Untitled';
  const value = contactValue(target);
  return value ? `${name} · ${value}` : name;
}

function eventDisplayLabel(rule: NotificationRule, options: EventTypeOption[]) {
  if (rule.event_type === STAFF_MESSAGE) {
    if (rule.applies_to_all_voicemails || !rule.category_label)
      return 'Voicemails';
    return `Voicemails · ${rule.category_label}`;
  }
  return (
    options.find((option) => option.value === rule.event_type)?.label ||
    rule.event_type
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

function targetIds(rule: NotificationRule) {
  return (rule.targets || [])
    .map((target) => target.id)
    .filter((id): id is number => typeof id === 'number');
}

async function ensureOk(
  response: Response | null | undefined,
  fallback: string,
) {
  if (!response?.ok) {
    throw new Error(await readError(response, fallback));
  }
  return response;
}

interface NotificationManagerProps {
  mode: 'store' | 'admin';
  stores?: NotificationStore[];
  storeId?: number;
  takesEvents?: boolean;
  takesCatering?: boolean;
}

const EVENT_INQUIRY = 'event-inquiry';
const CATERING_INQUIRY = 'catering-inquiry';

function unavailableEventReason(
  eventType: string,
  takesEvents: boolean,
  takesCatering: boolean,
): string | null {
  if (eventType === EVENT_INQUIRY && !takesEvents) {
    return 'Events are off for this store.';
  }
  if (eventType === CATERING_INQUIRY && !takesCatering) {
    return 'Catering is off for this store.';
  }
  return null;
}

const NotificationManager: React.FC<NotificationManagerProps> = ({
  mode,
  stores = [],
  storeId: scopedStoreId,
  takesEvents = false,
  takesCatering = false,
}) => {
  const { user } = useAuth();
  const [storeFilter, setStoreFilter] = useState<number | ''>('');
  const [targets, setTargets] = useState<NotificationTarget[]>([]);
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [subscriptionSort, setSubscriptionSort] = useState<
    TableSortState<'contact' | 'channel' | 'events' | 'store' | 'status'>
  >({ key: null, direction: 'asc' });
  const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
  const [dialogTargets, setDialogTargets] = useState<NotificationTarget[]>([]);
  const [dialogEventTypes, setDialogEventTypes] = useState<EventTypeOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editor, setEditor] = useState<EditorState>(emptyEditor());
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const storeId =
        mode === 'admin' ? storeFilter || undefined : scopedStoreId;
      const [targetResponse, ruleResponse, eventResponse] = await Promise.all([
        api.get(apiEndpoints.getNotificationTargets(storeId)),
        api.get(apiEndpoints.getNotificationRules(storeId)),
        api.get(
          apiEndpoints.getNotificationEventTypes(
            storeId,
            mode === 'store' ? { fullCatalog: true } : undefined,
          ),
        ),
      ]);
      await ensureOk(targetResponse, 'Failed to load contacts');
      await ensureOk(ruleResponse, 'Failed to load notifications');
      await ensureOk(eventResponse, 'Failed to load event types');
      const targetData = await targetResponse!.json();
      const ruleData = await ruleResponse!.json();
      const eventData = await eventResponse!.json();
      setTargets(asList(targetData));
      setRules(asList(ruleData));
      setEventTypes(
        asList(eventData).map((item: EventTypeOption) =>
          item.value === STAFF_MESSAGE
            ? { ...item, label: 'Voicemails' }
            : item,
        ),
      );
    } catch (err) {
      setTargets([]);
      setRules([]);
      setError(
        err instanceof Error ? err.message : 'Failed to load notifications',
      );
    } finally {
      setLoading(false);
    }
  }, [mode, scopedStoreId, storeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDialogChoices = useCallback(
    async (storeId: number | '') => {
      if (mode === 'admin' && !storeId) {
        setDialogTargets([]);
        setDialogEventTypes([]);
        return;
      }
      const queryId = mode === 'admin' ? storeId || undefined : scopedStoreId;
      const [targetResponse, eventResponse] = await Promise.all([
        api.get(apiEndpoints.getNotificationTargets(queryId)),
        api.get(
          apiEndpoints.getNotificationEventTypes(
            queryId,
            mode === 'store' ? { fullCatalog: true } : undefined,
          ),
        ),
      ]);
      await ensureOk(targetResponse, 'Failed to load contacts');
      await ensureOk(eventResponse, 'Failed to load event types');
      const targetData = await targetResponse!.json();
      const eventData = await eventResponse!.json();
      setDialogTargets(asList(targetData));
      setDialogEventTypes(
        asList(eventData).map((item: EventTypeOption) =>
          item.value === STAFF_MESSAGE
            ? { ...item, label: 'Voicemails' }
            : item,
        ),
      );
    },
    [mode, scopedStoreId],
  );

  const storeName = useCallback(
    (storeId: number) =>
      stores.find((store) => store.id === storeId)?.name || String(storeId),
    [stores],
  );

  const rows = useMemo<SubscriptionRow[]>(() => {
    const grouped = new Map<number, SubscriptionRow>();
    rules.forEach((rule) => {
      (rule.targets || []).forEach((ruleTarget) => {
        if (!ruleTarget.id) return;
        const known = targets.find((target) => target.id === ruleTarget.id);
        const target: NotificationTarget = known || {
          id: ruleTarget.id,
          store: rule.store,
          channel: ruleTarget.channel,
          email: ruleTarget.email,
          phone_e164: ruleTarget.phone_e164,
          label: ruleTarget.label || '',
          is_enabled: ruleTarget.is_enabled !== false,
        };
        const event = { rule, label: eventDisplayLabel(rule, eventTypes) };
        const existing = grouped.get(target.id);
        if (existing) {
          existing.events.push(event);
        } else {
          grouped.set(target.id, { target, events: [event] });
        }
      });
    });
    return Array.from(grouped.values());
  }, [rules, targets, eventTypes]);

  const visibleRows = useMemo(
    () =>
      filterAndSortRows(
        rows,
        subscriptionSearch,
        (row) =>
          rowSearchText([
            row.target.label,
            contactValue(row.target),
            row.target.channel,
            row.events.map((event) => event.label).join(' '),
            storeName(row.target.store),
            row.target.is_enabled ? 'Enabled' : 'Disabled',
          ]),
        subscriptionSort,
        (row, key) => {
          if (key === 'contact') {
            return row.target.label || contactValue(row.target);
          }
          if (key === 'channel') return row.target.channel;
          if (key === 'events')
            return row.events.map((event) => event.label).join(', ');
          if (key === 'store') return storeName(row.target.store);
          return row.target.is_enabled ? 'Enabled' : 'Disabled';
        },
      ),
    [rows, storeName, subscriptionSearch, subscriptionSort],
  );

  const openCreate = () => {
    const storeId =
      mode === 'store' ? scopedStoreId || user?.store_id || '' : storeFilter;
    setError('');
    setSuccess('');
    setEditor({ ...emptyEditor(storeId), open: true });
    void loadDialogChoices(storeId).catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    });
  };

  const openEdit = (row: SubscriptionRow) => {
    const voicemailEvents = row.events.filter(
      (event) => event.rule.event_type === STAFF_MESSAGE,
    );
    const allVoicemail = voicemailEvents.find(
      (event) => event.rule.applies_to_all_voicemails,
    );
    const category = voicemailEvents.find(
      (event) =>
        !event.rule.applies_to_all_voicemails && event.rule.category_label,
    );
    const selectedEvents = Array.from(
      new Set(
        row.events
          .filter((event) => event.rule.event_type !== STAFF_MESSAGE)
          .map((event) => event.rule.event_type),
      ),
    );
    if (voicemailEvents.length > 0) selectedEvents.push(STAFF_MESSAGE);
    setError('');
    setSuccess('');
    setOpenMenuId(null);
    setEditor({
      ...emptyEditor(row.target.store),
      open: true,
      mode: 'edit',
      contactChoice: String(row.target.id),
      label: row.target.label || '',
      channel: row.target.channel,
      email: row.target.email || '',
      phone: row.target.phone_e164 || '',
      eventTypes: selectedEvents,
      voicemailScope: allVoicemail ? 'all' : 'category',
      categoryLabel: category?.rule.category_label || '',
      categoryDescription: category?.rule.category_description || '',
      additionalContext: category?.rule.additional_context || '',
      routingPriority: String(category?.rule.routing_priority || 0),
      categoryRuleId: category?.rule.id || null,
      allVoicemailRuleId: allVoicemail?.rule.id || null,
      initialVoicemailScope: allVoicemail ? 'all' : 'category',
    });
    void loadDialogChoices(row.target.store).catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    });
  };

  const toggleEvent = (value: string) => {
    setEditor((prev) => {
      const selected = prev.eventTypes.includes(value);
      let eventTypes: string[];
      if (value === ALL_EVENTS) {
        eventTypes = selected ? [] : [ALL_EVENTS];
      } else if (selected) {
        eventTypes = prev.eventTypes.filter((item) => item !== value);
      } else {
        eventTypes = [
          ...prev.eventTypes.filter((item) => item !== ALL_EVENTS),
          value,
        ];
      }
      return {
        ...prev,
        eventTypes,
        voicemailScope: eventTypes.includes(STAFF_MESSAGE)
          ? prev.voicemailScope
          : 'all',
      };
    });
  };

  const unlinkTarget = async (rule: NotificationRule, targetId: number) => {
    const remaining = targetIds(rule).filter((id) => id !== targetId);
    const response =
      remaining.length === 0
        ? await api.delete(apiEndpoints.deleteNotificationRule(rule.id))
        : await api.patch(apiEndpoints.updateNotificationRule(rule.id), {
            target_ids: remaining,
          });
    await ensureOk(response, 'Failed to update a notification');
  };

  const createSubscriptions = async (
    storeId: number,
    eventTypesToAdd: string[],
    targetId: number | null,
    contact: Record<string, unknown> | null,
    voicemail: {
      scope: 'all' | 'category';
      categoryLabel: string;
      categoryDescription: string;
      additionalContext: string;
      routingPriority: number;
    } | null,
  ) => {
    if (eventTypesToAdd.length === 0) return;
    const payload: Record<string, unknown> = {
      store: storeId,
      event_types: eventTypesToAdd,
      is_enabled: true,
    };
    if (contact) payload.contact = contact;
    else payload.target_ids = [targetId];
    if (eventTypesToAdd.includes(STAFF_MESSAGE) && voicemail) {
      if (voicemail.scope === 'all') {
        payload.applies_to_all_voicemails = true;
      } else {
        payload.category_label = voicemail.categoryLabel;
        payload.category_description = voicemail.categoryDescription;
        payload.additional_context = voicemail.additionalContext;
        payload.routing_priority = voicemail.routingPriority;
      }
    }
    const response = await api.post(
      apiEndpoints.createNotificationRule(),
      payload,
    );
    await ensureOk(response, 'Failed to save the notification');
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const storeId = Number(editor.storeId);
    if (!storeId) {
      setError('Select a store.');
      return;
    }
    if (editor.eventTypes.length === 0) {
      setError('Select at least one event.');
      return;
    }
    const wantsVoicemail = editor.eventTypes.includes(STAFF_MESSAGE);
    const voicemailScope = mode === 'store' ? 'all' : editor.voicemailScope;
    if (
      wantsVoicemail &&
      voicemailScope === 'category' &&
      !editor.categoryLabel.trim()
    ) {
      setError('Enter a voicemail category, or choose all voicemails.');
      return;
    }
    const creatingContact =
      editor.mode === 'create' && editor.contactChoice === NEW_CONTACT;
    if (editor.mode === 'create' && !editor.contactChoice) {
      setError('Choose a contact or add a new one.');
      return;
    }
    if (creatingContact && editor.channel === 'email' && !editor.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (creatingContact && editor.channel === 'sms' && !editor.phone.trim()) {
      setError(
        'Phone is required. Use a country code, for example +15551234567.',
      );
      return;
    }

    const voicemail = wantsVoicemail
      ? {
          scope: voicemailScope,
          categoryLabel: editor.categoryLabel.trim(),
          categoryDescription: editor.categoryDescription.trim(),
          additionalContext: editor.additionalContext.trim(),
          routingPriority: Number(editor.routingPriority) || 0,
        }
      : null;

    setSaving(true);
    try {
      if (editor.mode === 'create') {
        await createSubscriptions(
          storeId,
          editor.eventTypes,
          creatingContact ? null : Number(editor.contactChoice),
          creatingContact
            ? {
                channel: editor.channel,
                label: editor.label.trim(),
                email: editor.channel === 'email' ? editor.email.trim() : null,
                phone_e164:
                  editor.channel === 'sms' ? editor.phone.trim() : null,
                is_enabled: true,
              }
            : null,
          voicemail,
        );
        setSuccess('Notification saved.');
      } else {
        const targetId = Number(editor.contactChoice);
        const current = rules.filter((rule) =>
          targetIds(rule).includes(targetId),
        );
        const currentNonVoicemail = current.filter(
          (rule) => rule.event_type !== STAFF_MESSAGE,
        );
        const desiredNonVoicemail = editor.eventTypes.filter(
          (value) => value !== STAFF_MESSAGE,
        );
        const toAdd = desiredNonVoicemail.filter(
          (value) =>
            !currentNonVoicemail.some((rule) => rule.event_type === value),
        );
        const toRemove = currentNonVoicemail.filter(
          (rule) => !desiredNonVoicemail.includes(rule.event_type),
        );
        for (const rule of toRemove) {
          await unlinkTarget(rule, targetId);
        }

        const voicemailRules = current.filter(
          (rule) => rule.event_type === STAFF_MESSAGE,
        );
        let addVoicemail = false;
        if (!wantsVoicemail) {
          for (const rule of voicemailRules) {
            await unlinkTarget(rule, targetId);
          }
        } else if (voicemailScope === 'all') {
          if (!voicemailRules.some((rule) => rule.applies_to_all_voicemails)) {
            addVoicemail = true;
          }
          if (editor.initialVoicemailScope === 'category') {
            const shownCategory = voicemailRules.find(
              (rule) => rule.id === editor.categoryRuleId,
            );
            if (shownCategory) await unlinkTarget(shownCategory, targetId);
          }
        } else {
          const allRule = voicemailRules.find(
            (rule) => rule.applies_to_all_voicemails,
          );
          if (allRule) await unlinkTarget(allRule, targetId);
          const shownCategory = voicemailRules.find(
            (rule) => rule.id === editor.categoryRuleId,
          );
          const sameCategory =
            shownCategory &&
            shownCategory.category_label === editor.categoryLabel.trim();
          if (sameCategory) {
            const response = await api.patch(
              apiEndpoints.updateNotificationRule(shownCategory.id),
              {
                category_description: editor.categoryDescription.trim(),
                additional_context: editor.additionalContext.trim(),
                routing_priority: Number(editor.routingPriority) || 0,
              },
            );
            await ensureOk(response, 'Failed to update the voicemail category');
          } else {
            if (shownCategory) await unlinkTarget(shownCategory, targetId);
            addVoicemail = true;
          }
        }

        await createSubscriptions(
          storeId,
          addVoicemail ? [...toAdd, STAFF_MESSAGE] : toAdd,
          targetId,
          null,
          addVoicemail ? voicemail : null,
        );
        setSuccess('Notification updated.');
      }
      setEditor(emptyEditor());
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save the notification',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: SubscriptionRow) => {
    setError('');
    setSuccess('');
    setOpenMenuId(null);
    try {
      const response = await api.patch(
        apiEndpoints.updateNotificationTarget(row.target.id),
        { is_enabled: !row.target.is_enabled },
      );
      await ensureOk(response, 'Failed to update the contact');
      setSuccess(
        row.target.is_enabled ? 'Contact disabled.' : 'Contact enabled.',
      );
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update the contact',
      );
    }
  };

  const handleRemove = async (row: SubscriptionRow) => {
    setError('');
    setSuccess('');
    setOpenMenuId(null);
    try {
      for (const event of row.events) {
        await unlinkTarget(event.rule, row.target.id);
      }
      setSuccess('Notification removed.');
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to remove the notification',
      );
    }
  };

  const contactOptions = [
    ...dialogTargets.map((target) => ({
      value: String(target.id),
      label: contactLabel(target),
    })),
    { value: NEW_CONTACT, label: 'Add a new contact' },
  ];

  const visibleEventTypes =
    editor.mode === 'edit' || editor.storeId ? dialogEventTypes : eventTypes;

  return (
    <ComponentCard
      title="Who to notify"
      description="Choose who gets an email or text when something happens."
      className="overflow-hidden"
    >
      <div className="space-y-6">
        {mode === 'admin' && (
          <div className="max-w-md">
            <Label>Filter by Store</Label>
            <SearchableSelect
              options={[
                { value: '', label: 'All stores' },
                ...stores.map((store) => ({
                  value: store.id,
                  label: `${store.name} (ID: ${store.id})`,
                })),
              ]}
              value={storeFilter}
              onChange={(value) => setStoreFilter(value ? Number(value) : '')}
              placeholder="All stores"
            />
          </div>
        )}

        <div className="max-w-md">
          <Label>Search</Label>
          <Input
            type="search"
            aria-label="Search notification subscriptions"
            value={subscriptionSearch}
            onChange={(event) => setSubscriptionSearch(event.target.value)}
            placeholder="Search contact, channel, event, or store"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={openCreate}
            className={buttonClassName}
          >
            Add notification
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-500 dark:border-success-800 dark:bg-success-900/20">
            {success}
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
          <Table>
            <TableHeader className="border-b border-gray-100 dark:border-white/5">
              <TableRow>
                {(
                  [
                    ['contact', 'Contact'],
                    ['channel', 'Channel'],
                    ['events', 'Events'],
                    ...(mode === 'admin' ? [['store', 'Store'] as const] : []),
                    ['status', 'Status'],
                  ] as const
                ).map(([key, label]) => (
                  <TableCell
                    key={key}
                    isHeader
                    className="px-4 py-3 text-left text-theme-xs text-gray-500 dark:text-gray-400"
                  >
                    <button
                      type="button"
                      aria-label={`Sort by ${label}`}
                      onClick={() =>
                        setSubscriptionSort((current) =>
                          toggleSortState(current, key),
                        )
                      }
                    >
                      {label}
                      {subscriptionSort.key === key
                        ? subscriptionSort.direction === 'asc'
                          ? ' ↑'
                          : ' ↓'
                        : ''}
                    </button>
                  </TableCell>
                ))}
                <TableCell
                  isHeader
                  className="px-4 py-3 text-right text-theme-xs text-gray-500 dark:text-gray-400"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={mode === 'admin' ? 6 : 5}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    {loading
                      ? 'Loading...'
                      : subscriptionSearch.trim()
                        ? 'No notifications match that search.'
                        : 'No notifications yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow key={row.target.id}>
                    <TableCell className="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
                      <div>{row.target.label || 'Untitled'}</div>
                      <div className="text-xs text-gray-500">
                        {contactValue(row.target) || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm capitalize text-gray-800 dark:text-white/90">
                      {row.target.channel}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
                      <div className="flex flex-wrap gap-2">
                        {row.events.map((event) => (
                          <span
                            key={event.rule.id}
                            className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200"
                          >
                            {event.label}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    {mode === 'admin' && (
                      <TableCell className="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
                        {storeName(row.target.store)}
                      </TableCell>
                    )}
                    <TableCell className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                          row.target.is_enabled
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {row.target.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm">
                      <div className="relative inline-block text-left">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenMenuId((current) =>
                              current === row.target.id ? null : row.target.id,
                            )
                          }
                          className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                        >
                          ···
                        </button>
                        {openMenuId === row.target.id && (
                          <div className="absolute right-0 top-full z-30 mt-2 w-36 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-gray-800">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleToggle(row)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              {row.target.is_enabled ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleRemove(row)}
                              className="block w-full px-4 py-2 text-left text-xs text-error-600 hover:bg-error-50 dark:text-error-300"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Modal
        isOpen={editor.open}
        onClose={() => setEditor(emptyEditor())}
        className="max-w-3xl p-6 lg:p-8"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-800 dark:text-white">
          {editor.mode === 'create' ? 'Add notification' : 'Edit notification'}
        </h3>
        <form onSubmit={handleSave} className="space-y-4">
          {mode === 'admin' && (
            <div>
              <Label>
                Store <span className="text-error-500">*</span>
              </Label>
              <SearchableSelect
                options={stores.map((store) => ({
                  value: store.id,
                  label: `${store.name} (ID: ${store.id})`,
                }))}
                value={editor.storeId}
                onChange={(value) => {
                  const storeId = value ? Number(value) : '';
                  setEditor((prev) => ({
                    ...prev,
                    storeId,
                    contactChoice:
                      prev.mode === 'edit' ? prev.contactChoice : '',
                  }));
                  void loadDialogChoices(storeId).catch((err) => {
                    setError(
                      err instanceof Error
                        ? err.message
                        : 'Failed to load contacts',
                    );
                  });
                }}
                placeholder="Select a store"
                disabled={editor.mode === 'edit'}
              />
            </div>
          )}

          <div>
            <Label>
              Contact <span className="text-error-500">*</span>
            </Label>
            {editor.mode === 'edit' ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 dark:border-white/10 dark:bg-gray-800 dark:text-gray-200">
                {contactLabel({
                  label: editor.label,
                  channel: editor.channel,
                  email: editor.email,
                  phone_e164: editor.phone,
                })}
              </div>
            ) : (
              <SearchableSelect
                options={contactOptions}
                value={editor.contactChoice}
                onChange={(value) =>
                  setEditor((prev) => ({ ...prev, contactChoice: value }))
                }
                placeholder={
                  editor.storeId ? 'Select a contact' : 'Select a store first'
                }
                disabled={mode === 'admin' && !editor.storeId}
              />
            )}
          </div>

          {editor.mode === 'create' && editor.contactChoice === NEW_CONTACT && (
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 p-4 dark:border-white/10 md:grid-cols-2">
              <div>
                <Label>Label</Label>
                <Input
                  name="label"
                  value={editor.label}
                  placeholder="Manager"
                  onChange={(inputEvent) =>
                    setEditor((prev) => ({
                      ...prev,
                      label: inputEvent.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label>Channel</Label>
                <select
                  value={editor.channel}
                  onChange={(inputEvent) =>
                    setEditor((prev) => ({
                      ...prev,
                      channel: inputEvent.target.value as 'email' | 'sms',
                      email: '',
                      phone: '',
                    }))
                  }
                  className={fieldClassName}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              {editor.channel === 'email' ? (
                <div className="md:col-span-2">
                  <Label>
                    Email <span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={editor.email}
                    placeholder="manager@example.com"
                    onChange={(inputEvent) =>
                      setEditor((prev) => ({
                        ...prev,
                        email: inputEvent.target.value,
                      }))
                    }
                    required
                  />
                </div>
              ) : (
                <div className="md:col-span-2">
                  <Label>
                    Phone <span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="tel"
                    value={editor.phone}
                    placeholder="+15551234567"
                    hint="Include the country code, for example +15551234567."
                    onChange={(inputEvent) =>
                      setEditor((prev) => ({
                        ...prev,
                        phone: inputEvent.target.value,
                      }))
                    }
                    required
                  />
                </div>
              )}
            </div>
          )}

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Events <span className="text-error-500">*</span>
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {visibleEventTypes.map((option) => {
                const unavailable =
                  mode === 'store'
                    ? unavailableEventReason(
                        option.value,
                        takesEvents,
                        takesCatering,
                      )
                    : null;
                return (
                  <label
                    key={option.value}
                    className={`flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700 dark:border-white/10 dark:text-gray-200 ${
                      unavailable
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={editor.eventTypes.includes(option.value)}
                      disabled={Boolean(unavailable)}
                      onChange={() => toggleEvent(option.value)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                    />
                    <span>
                      <span className="block">
                        {option.value === STAFF_MESSAGE
                          ? 'Voicemails'
                          : option.label}
                      </span>
                      {unavailable ? (
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {unavailable}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {mode === 'admin' && editor.eventTypes.includes(STAFF_MESSAGE) && (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-white/10">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name="voicemailScope"
                  checked={editor.voicemailScope === 'all'}
                  onChange={() =>
                    setEditor((prev) => ({ ...prev, voicemailScope: 'all' }))
                  }
                />
                All voicemails
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name="voicemailScope"
                  checked={editor.voicemailScope === 'category'}
                  onChange={() =>
                    setEditor((prev) => ({
                      ...prev,
                      voicemailScope: 'category',
                    }))
                  }
                />
                Only a category
              </label>
              {editor.voicemailScope === 'category' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label>
                        Category <span className="text-error-500">*</span>
                      </Label>
                      <input
                        value={editor.categoryLabel}
                        onChange={(inputEvent) =>
                          setEditor((prev) => ({
                            ...prev,
                            categoryLabel: inputEvent.target.value,
                          }))
                        }
                        placeholder="Lost & Found"
                        className={fieldClassName}
                        required
                      />
                    </div>
                    <div>
                      <Label>Routing priority</Label>
                      <input
                        type="number"
                        value={editor.routingPriority}
                        onChange={(inputEvent) =>
                          setEditor((prev) => ({
                            ...prev,
                            routingPriority: inputEvent.target.value,
                          }))
                        }
                        className={fieldClassName}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Category description</Label>
                    <textarea
                      value={editor.categoryDescription}
                      onChange={(inputEvent) =>
                        setEditor((prev) => ({
                          ...prev,
                          categoryDescription: inputEvent.target.value,
                        }))
                      }
                      rows={2}
                      className={fieldClassName}
                    />
                  </div>
                  <div>
                    <Label>Additional context</Label>
                    <textarea
                      value={editor.additionalContext}
                      onChange={(inputEvent) =>
                        setEditor((prev) => ({
                          ...prev,
                          additionalContext: inputEvent.target.value,
                        }))
                      }
                      rows={3}
                      className={fieldClassName}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditor(emptyEditor())}
              className={quietButtonClassName}
            >
              Cancel
            </button>
            <button type="submit" className={buttonClassName} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </ComponentCard>
  );
};

export default NotificationManager;
